import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

export default function ProcureFunds() {
  const [capital, setCapital] = useState(200);
  const [activeStep, setActiveStep] = useState(1);
  const [formData, setFormData] = useState({
    fundingAmount: 5000, // starts at $5K

    // Step 2: Business Details
    businessName: "",
    businessWebsite: "",
    annualRevenue: "",

    // Step 3: Personal Details
    firstName: "",
    lastName: "",
    email: "",
  });

  const [captchaToken, setCaptchaToken] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedTime, setSelectedTime] = useState("");

  const steps = [
    { number: 1, label: "Funding Amount" },
    { number: 2, label: "Business Details" },
    { number: 3, label: "Personal Details" },
    { number: 4, label: "Verification" },
  ];

  const timeSlots = [
    "09:00 AM",
    "09:30 AM",
    "10:00 AM",
    "10:30 AM",
    "11:00 AM",
    "11:30 AM",
    "02:00 PM",
    "02:30 PM",
    "03:00 PM",
    "03:30 PM",
    "04:00 PM",
    "04:30 PM",
  ];

  const isValidURL = (url) => {
    try {
      const parsed = new URL(url);
      return ["http:", "https:"].includes(parsed.protocol);
    } catch (_) {
      return false;
    }
  };

  const isValidEmail = (email) => {
    const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return pattern.test(email);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const nextStep = () => {
    if (activeStep < steps.length) {
      setActiveStep(activeStep + 1);
    }
  };

  const prevStep = () => {
    if (activeStep > 1) {
      setActiveStep(activeStep - 1);
    }
  };

  const verifyCaptcha = (token) => {
    setCaptchaToken(token);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!captchaToken) {
      return;
    }

    if (!isValidEmail(formData.email)) {
      return;
    }

    console.log("Form submitted:", formData);
    console.log("CAPTCHA Token:", captchaToken);

    // Simulate API call
    setTimeout(() => {
      setIsSubmitted(true);
    }, 1000);
  };

  const handleScheduleAppointment = () => {
    if (!selectedDate || !selectedTime) {
      return;
    }

    console.log("Appointment scheduled:", {
      ...formData,
      appointmentDate: selectedDate,
      appointmentTime: selectedTime,
    });

    // Reset everything
    setCaptchaToken("");
    setActiveStep(1);
    setIsSubmitted(false);
    setSelectedDate("");
    setSelectedTime("");
    setFormData({
      fundingAmount: 5000,
      businessName: "",
      businessWebsite: "",
      annualRevenue: "",
      firstName: "",
      lastName: "",
      email: "",
    });
  };

  const isStepValid = () => {
    switch (activeStep) {
      case 1:
        return formData.fundingAmount >= 5000;
      case 2:
        return (
          formData.businessName.trim() &&
          formData.businessWebsite.trim() &&
          isValidURL(formData.businessWebsite) &&
          formData.annualRevenue
        );
      case 3:
        return (
          formData.firstName.trim() &&
          formData.lastName.trim() &&
          formData.email.trim() &&
          isValidEmail(formData.email)
        );
      case 4:
        return captchaToken !== "";
      default:
        return false;
    }
  };

  // Get tomorrow's date for min date
  const getTomorrowDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split("T")[0];
  };

  // Get date 30 days from now for max date
  const getMaxDate = () => {
    const maxDate = new Date();
    maxDate.setDate(maxDate.getDate() + 30);
    return maxDate.toISOString().split("T")[0];
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center px-4 sm:px-6 lg:px-12 py-16 tracking-wide font-sans">
        <div className="max-w-2xl w-full">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl p-8 shadow-2xl border border-gray-100"
          >
            {/* Success Message */}
            <div className="text-center mb-8">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring" }}
                className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6"
              >
                <svg
                  className="w-10 h-10 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </motion.div>

              <h2 className="text-3xl font-bold text-gray-900 mb-4 font-mokoto">
                Application Submitted Successfully!
              </h2>

              <p className="text-xl text-gray-600 mb-2">
                Thank you,{" "}
                <span className="font-semibold text-green-900">
                  {formData.firstName}
                </span>
                !
              </p>

              <p className="text-gray-600 mb-6">
                Your funding application for{" "}
                {formatCurrency(formData.fundingAmount)} has been received.
              </p>

              <div className="bg-green-50 rounded-xl p-4 border border-green-200 max-w-md mx-auto">
                <p className="text-green-700 font-medium">
                  📧 We've sent a confirmation email to {formData.email}
                </p>
              </div>
            </div>

            {/* Schedule Appointment Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="border-t border-gray-200 pt-8"
            >
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-2 font-mokoto">
                  Schedule Your Consultation
                </h3>
                <p className="text-gray-600">
                  Choose a convenient time for your funding consultation
                </p>
              </div>

              <div className="space-y-6">
                {/* Date Selection */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-3 text-lg">
                    Select Date
                  </label>
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    min={getTomorrowDate()}
                    max={getMaxDate()}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200 text-lg"
                  />
                  <p className="text-sm text-gray-500 mt-2">
                    Available dates: {getTomorrowDate()} to {getMaxDate()}
                  </p>
                </div>

                {/* Time Selection */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-3 text-lg">
                    Select Time Slot
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {timeSlots.map((time) => (
                      <button
                        key={time}
                        type="button"
                        onClick={() => setSelectedTime(time)}
                        className={`py-3 px-4 rounded-xl border-2 font-medium transition-all duration-200 ${
                          selectedTime === time
                            ? "border-green-500 bg-green-50 text-green-700 shadow-sm"
                            : "border-gray-200 bg-white text-gray-700 hover:border-green-300 hover:bg-green-25"
                        }`}
                      >
                        {time}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Selected Appointment Summary */}
                {selectedDate && selectedTime && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="bg-blue-50 rounded-xl p-4 border border-blue-200"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-blue-900">
                          Selected Appointment
                        </p>
                        <p className="text-blue-700">
                          {new Date(selectedDate).toLocaleDateString("en-US", {
                            weekday: "long",
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          })}{" "}
                          at {selectedTime}
                        </p>
                      </div>
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <svg
                          className="w-6 h-6 text-blue-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Schedule Button */}
                <motion.button
                  whileHover={{
                    scale: selectedDate && selectedTime ? 1.02 : 1,
                  }}
                  whileTap={{ scale: selectedDate && selectedTime ? 0.98 : 1 }}
                  onClick={handleScheduleAppointment}
                  disabled={!selectedDate || !selectedTime}
                  className={`w-full py-4 rounded-xl font-semibold text-lg transition-all duration-200 ${
                    selectedDate && selectedTime
                      ? "bg-green-900 hover:bg-green-800 text-white shadow-lg cursor-pointer"
                      : "bg-gray-300 text-gray-500 cursor-not-allowed"
                  }`}
                >
                  Confirm Appointment
                </motion.button>

                {/* Skip Option */}
                <button
                  onClick={() => {
                    setCaptchaToken("");
                    setActiveStep(1);
                    setIsSubmitted(false);
                    setFormData({
                      fundingAmount: 5000,
                      businessName: "",
                      businessWebsite: "",
                      annualRevenue: "",
                      firstName: "",
                      lastName: "",
                      email: "",
                    });
                  }}
                  className="w-full text-gray-600 hover:text-gray-800 font-medium py-3 transition duration-200"
                >
                  Skip scheduling for now
                </button>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center px-4 sm:px-6 lg:px-12 py-16 tracking-wide font-sans">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        {/* Left Content */}
        <div className="space-y-8">
          {/* Header */}
          <div className="space-y-6">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-2xl md:text-4xl font-bold text-gray-900 leading-tight font-mokoto"
            >
              Fast, Flexible Capital{" "}
              <span className="text-transparent bg-clip-text bg-green-900">
                Up to $5M
              </span>{" "}
              for Growing B2B SaaS
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-xl text-gray-600 leading-relaxed"
            >
              Get capital in under 30 days with a transparent, flexible
              structure —
              <span className="font-semibold text-gray-800">
                {" "}
                no equity dilution
              </span>
              , no penalties for prepayment, and terms up to 24 months.
            </motion.p>
          </div>

          {/* Multi-Step Form Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white rounded-2xl p-8 shadow-2xl border border-gray-100 max-w-lg"
          >
            {/* Progress Steps */}
            <div className="flex justify-between mb-8">
              {steps.map((step) => (
                <div key={step.number} className="flex flex-col items-center">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm transition-all duration-300 ${
                      step.number === activeStep
                        ? "bg-green-900 text-white shadow-lg scale-110"
                        : step.number < activeStep
                        ? "bg-green-100 text-green-900"
                        : "bg-gray-100 text-gray-400"
                    }`}
                  >
                    {step.number < activeStep ? "✓" : step.number}
                  </div>
                  <span
                    className={`text-xs mt-2 font-medium text-center ${
                      step.number === activeStep
                        ? "text-gray-900"
                        : "text-gray-500"
                    }`}
                  >
                    {step.label}
                  </span>
                </div>
              ))}
            </div>

            <AnimatePresence mode="wait">
              {activeStep === 1 && (
                <motion.div
                  key="step1"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-6"
                >
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2 font-mokoto">
                      Funding Amount
                    </h3>
                    <p className="text-gray-600 mb-6">
                      How much capital does your business need?
                    </p>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <div className="flex justify-between items-center mb-4">
                        <label className="text-gray-700 font-semibold">
                          Desired Funding Amount
                        </label>
                        <span className="font-mokoto text-2xl font-bold text-transparent bg-clip-text bg-green-900">
                          {formatCurrency(formData.fundingAmount)}
                        </span>
                      </div>

                      <input
                        type="range"
                        min="5000"
                        max="5000000"
                        // step="50000"
                        value={formData.fundingAmount}
                        onChange={(e) =>
                          handleInputChange(
                            "fundingAmount",
                            Number(e.target.value)
                          )
                        }
                        className="w-full h-3 bg-gradient-to-r from-blue-200 to-green-200 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-6 [&::-webkit-slider-thumb]:w-6 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-gradient-to-r [&::-webkit-slider-thumb]:from-green-900 [&::-webkit-slider-thumb]:to-green-900 [&::-webkit-slider-thumb]:shadow-lg"
                      />

                      <div className="flex justify-between text-sm text-gray-500 mt-2 font-mokoto">
                        <span>$5K</span>
                        <span>$5M</span>
                      </div>
                    </div>

                    <div className="bg-gradient-to-r from-blue-50 to-green-50 rounded-xl p-4 border border-blue-100">
                      <p className="text-sm text-gray-600 text-center">
                        💡 Most SaaS companies get a quote in 48 hours and
                        capital in under 30 days
                      </p>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Step 2: Business Details */}
              {activeStep === 2 && (
                <motion.div
                  key="step2"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-6"
                >
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">
                      Business Details
                    </h3>
                    <p className="text-gray-600 mb-6">
                      Tell us about your company
                    </p>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-gray-700 font-semibold mb-2">
                        Business Name *
                      </label>
                      <input
                        type="text"
                        value={formData.businessName}
                        onChange={(e) =>
                          handleInputChange("businessName", e.target.value)
                        }
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                        placeholder="Enter your company name"
                      />
                    </div>

                    <div>
                      <label className="block text-gray-700 font-semibold mb-2">
                        Business Website *
                      </label>
                      <input
                        type="url"
                        value={formData.businessWebsite}
                        onChange={(e) =>
                          handleInputChange("businessWebsite", e.target.value)
                        }
                        className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:border-transparent transition-all duration-200 ${
                          formData.businessWebsite &&
                          !isValidURL(formData.businessWebsite)
                            ? "border-red-500 focus:ring-red-300"
                            : "border-gray-300 focus:ring-blue-500"
                        }`}
                        placeholder="https://example.com"
                      />
                      {formData.businessWebsite &&
                        !isValidURL(formData.businessWebsite) && (
                          <p className="text-red-600 text-sm mt-2">
                            Please enter a valid URL starting with http:// or
                            https://
                          </p>
                        )}
                    </div>

                    <div>
                      <label className="block text-gray-700 font-semibold mb-2">
                        Annual Revenue *
                      </label>
                      <select
                        value={formData.annualRevenue}
                        onChange={(e) =>
                          handleInputChange("annualRevenue", e.target.value)
                        }
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                      >
                        <option value="">Select revenue range</option>
                        <option value="0-100k">$0 - $100,000</option>
                        <option value="100k-500k">$100,000 - $500,000</option>
                        <option value="500k-1m">$500,000 - $1,000,000</option>
                        <option value="1m-5m">$1,000,000 - $5,000,000</option>
                        <option value="5m+">$5,000,000+</option>
                      </select>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Step 3: Personal Details */}
              {activeStep === 3 && (
                <motion.div
                  key="step3"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-6"
                >
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">
                      Personal Details
                    </h3>
                    <p className="text-gray-600 mb-6">
                      Almost done! Just need your contact information
                    </p>
                  </div>

                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-gray-700 font-semibold mb-2">
                          First Name *
                        </label>
                        <input
                          type="text"
                          value={formData.firstName}
                          onChange={(e) =>
                            handleInputChange("firstName", e.target.value)
                          }
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                          placeholder="John"
                        />
                      </div>
                      <div>
                        <label className="block text-gray-700 font-semibold mb-2">
                          Last Name *
                        </label>
                        <input
                          type="text"
                          value={formData.lastName}
                          onChange={(e) =>
                            handleInputChange("lastName", e.target.value)
                          }
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                          placeholder="Doe"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-gray-700 font-semibold mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) =>
                          handleInputChange("email", e.target.value)
                        }
                        className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:border-transparent transition-all duration-200 ${
                          formData.email && !isValidEmail(formData.email)
                            ? "border-red-500 focus:ring-red-300"
                            : "border-gray-300 focus:ring-blue-500"
                        }`}
                        placeholder="john@company.com"
                      />
                      {formData.email && !isValidEmail(formData.email) && (
                        <p className="text-red-600 text-sm mt-2">
                          Please enter a valid email address (e.g.,
                          name@domain.com)
                        </p>
                      )}
                    </div>

                    <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                      <p className="text-sm text-blue-700 text-center">
                        📧 We'll send your custom quote to this email address
                      </p>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Step 4: CAPTCHA Verification */}
              {activeStep === 4 && (
                <motion.div
                  key="step4"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-6"
                >
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">
                      Security Verification
                    </h3>
                    <p className="text-gray-600 mb-6">
                      Please verify you're not a robot
                    </p>
                  </div>

                  <div className="space-y-6">
                    <div className="bg-yellow-50 rounded-xl p-4 border border-yellow-200">
                      <p className="text-sm text-yellow-700 text-center">
                        🔒 For security purposes, please complete the
                        verification below
                      </p>
                    </div>

                    <div className="flex justify-center py-4">
                      <div className="bg-gray-50 rounded-xl p-6 border-2 border-dashed border-gray-300">
                        <div className="text-center">
                          <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                            <svg
                              className="w-8 h-8 text-gray-500"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                              />
                            </svg>
                          </div>

                          <p className="text-gray-700 mb-4 font-medium">
                            Security Check Required
                          </p>

                          <div className="space-y-4">
                            <button
                              type="button"
                              onClick={() =>
                                verifyCaptcha("verified_" + Date.now())
                              }
                              className={`w-full py-3 px-4 rounded-xl font-semibold transition-all duration-200 ${
                                captchaToken
                                  ? "bg-green-100 text-green-700 border border-green-300"
                                  : "bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300"
                              }`}
                            >
                              {captchaToken ? (
                                <span className="flex items-center justify-center">
                                  <svg
                                    className="w-5 h-5 mr-2"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                  Verified - I'm not a robot
                                </span>
                              ) : (
                                "Click to verify you're human"
                              )}
                            </button>

                            {captchaToken && (
                              <div className="bg-green-50 rounded-lg p-3 border border-green-200">
                                <p className="text-green-700 text-sm text-center">
                                  ✓ Verification complete! You can now submit
                                  your application.
                                </p>
                              </div>
                            )}
                          </div>

                          <p className="text-xs text-gray-500 mt-4">
                            This helps us prevent automated submissions and
                            protect your information.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-50 rounded-xl p-4">
                      <p className="text-sm text-gray-600 text-center">
                        📋 Review your information before submitting
                      </p>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8">
              <button
                onClick={prevStep}
                disabled={activeStep === 1}
                className={`px-6 py-3 rounded-xl font-semibold transition-all duration-200 ${
                  activeStep === 1
                    ? "text-gray-400 cursor-not-allowed"
                    : "text-gray-600 hover:text-gray-800 hover:bg-gray-100"
                }`}
              >
                ← Back
              </button>

              {activeStep < steps.length ? (
                <motion.button
                  whileHover={{ scale: isStepValid() ? 1.02 : 1 }}
                  whileTap={{ scale: isStepValid() ? 0.98 : 1 }}
                  onClick={nextStep}
                  disabled={!isStepValid()}
                  className={`px-8 py-3 rounded-xl font-semibold transition-all duration-200 ${
                    isStepValid()
                      ? "bg-green-900 hover:bg-blue-700 text-white shadow-lg"
                      : "bg-gray-300 text-gray-500 cursor-not-allowed"
                  }`}
                >
                  Continue →
                </motion.button>
              ) : (
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleSubmit}
                  disabled={!isStepValid()}
                  className={`px-8 py-3 rounded-xl font-semibold transition-all duration-200 ${
                    isStepValid()
                      ? "bg-green-900 hover:bg-blue-700 text-white shadow-lg"
                      : "bg-gray-300 text-gray-500 cursor-not-allowed"
                  }`}
                >
                  Submit Application
                </motion.button>
              )}
            </div>
          </motion.div>
        </div>

        {/* Right Visual Section - Same as before */}
        <div className="relative">
          {/* Main Image Container */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5 }}
            className="relative"
          >
            <div className="bg-gradient-to-br from-blue-500/10 to-green-500/10 rounded-3xl p-8 backdrop-blur-sm">
              <div className="bg-white rounded-2xl p-4 shadow-2xl overflow-hidden">
                {/* Professional Business Image */}
                <div className="relative h-[500px] rounded-xl overflow-hidden">
                  <img
                    src="https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80"
                    alt="Professional business person discussing funding options"
                    className="w-full h-full object-cover object-center"
                  />

                  {/* Image Overlay Gradient */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>

                  {/* Image Badge */}
                  <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm rounded-full px-3 py-1 flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-900 rounded-full animate-pulse"></div>
                    <span className="text-sm font-medium text-gray-700">
                      Live Funding
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Floating Elements */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              className="absolute -top-4 -left-4 bg-white rounded-2xl p-4 shadow-2xl border border-gray-100 max-w-xs z-10"
            >
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-green-900 rounded-xl flex items-center justify-center">
                  <svg
                    className="w-6 h-6 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Potential Funding</p>
                  <p className="text-lg font-bold text-gray-900 font-mokoto">
                    {formatCurrency(capital * 2)} -{" "}
                    {formatCurrency(capital * 3)}
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.8, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ delay: 0.9 }}
              className="absolute -bottom-4 -right-4 bg-green-900 rounded-2xl p-5 shadow-2xl text-white max-w-xs z-10"
            >
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                  <svg
                    className="w-5 h-5 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <div>
                  <p className="text-sm opacity-90">Total Available</p>
                  <p className="text-xl font-mokoto ">$5,000,000</p>
                </div>
              </div>
            </motion.div>

            {/* Additional Floating Stats Card */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8, x: -20 }}
              animate={{ opacity: 1, scale: 1, x: 0 }}
              transition={{ delay: 1.1 }}
              className="absolute top-1/2 -left-8 bg-white rounded-xl p-3 shadow-lg border border-gray-100 z-10"
            >
              <div className="text-center">
                <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-1">
                  <svg
                    className="w-4 h-4 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                    />
                  </svg>
                </div>
                <p className="text-xs text-gray-600">Avg. Time</p>
                <p className="text-sm font-bold text-gray-900">30 Days</p>
              </div>
            </motion.div>

            {/* Background Decorative Elements */}
            <div className="absolute -z-10 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-blue-200 to-green-200 rounded-full blur-3xl opacity-30"></div>

            {/* Additional Floating Shapes */}
            <motion.div
              animate={{
                y: [0, -20, 0],
                rotate: [0, 5, 0],
              }}
              transition={{
                duration: 6,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              className="absolute -bottom-8 left-10 w-16 h-16 bg-blue-200/30 rounded-full blur-xl"
            ></motion.div>

            <motion.div
              animate={{
                y: [0, 15, 0],
                rotate: [0, -5, 0],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 1,
              }}
              className="absolute -top-8 right-16 w-12 h-12 bg-green-200/30 rounded-full blur-xl"
            ></motion.div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
